import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useGetVehicleLicenseTypes } from "src/networking";
import { useLicenseTypesStore } from "src/storage/formik/formik-license.types.store";
import {
  DataEntryVehicleStepSectionProps,
  VehicleLicenseTypesMapper,
  YesNoAnswer,
} from "src/types";
import { useYesNoAnswer } from "src/utils";

export const useDataEntryVehicleStepDriver = ({
  formik,
  formikFieldBaseName,
  formikVehicleValues,
}: DataEntryVehicleStepSectionProps) => {
  const { t } = useTranslation();
  const { yesNoOptions } = useYesNoAnswer();
  const { crossGate: getVehicleLicenseTypes } = useGetVehicleLicenseTypes();
  const licenseTypes = useLicenseTypesStore((state) => state.licenseTypes);
  const setLicenseTypes = useLicenseTypesStore(
    (state) => state.setLicenseTypes,
  );

  const { setFieldValue, setFieldError, setFieldTouched } = formik;
  const { driver, contractor } = formikVehicleValues;

  const fieldsToModifyMap: Record<string, string> = {
    [`${formikFieldBaseName}.driver.name`]: "",
    [`${formikFieldBaseName}.driver.surname`]: "",
    [`${formikFieldBaseName}.driver.dateOfBirth`]: "",
    [`${formikFieldBaseName}.driver.fiscalCodeOrVAT`]: "",
    [`${formikFieldBaseName}.driver.address`]: "",
    [`${formikFieldBaseName}.driver.phoneNumber`]: "",
    [`${formikFieldBaseName}.driver.email`]: "",
    [`${formikFieldBaseName}.driver.licenseNumber`]: "",
    [`${formikFieldBaseName}.driver.category`]: "",
    [`${formikFieldBaseName}.driver.validUntil`]: "",
  };

  const fetchVehicleLicenseTypes = () => {
    getVehicleLicenseTypes(
      {},
      {
        onSuccess: (response) => {
          const licenseTypes = response.map((type) =>
            VehicleLicenseTypesMapper.fromDTO(type),
          );
          setLicenseTypes(licenseTypes);
        },
        onFailure: (error) => {
          console.error("Error fetching vehicle license types:", error);
        },
      },
    );
  };

  const disableDriverFieldIfDriverCoincidesWithContractor =
    driver.driverCoincidesWithContractor === YesNoAnswer.YES;

  useEffect(() => {
    if (licenseTypes.length > 0) return;

    fetchVehicleLicenseTypes();
  }, []);

  /***
   * If driverCoincidesWithContractor is 'NO' => Reset fields
   * If driverCoincidesWithContractor is 'YES' => Copy contractor values
   */
  useEffect(() => {
    if (!driver.driverCoincidesWithContractor) return;

    const newValuesMap = { ...fieldsToModifyMap };

    if (driver.driverCoincidesWithContractor === YesNoAnswer.YES) {
      newValuesMap[`${formikFieldBaseName}.driver.name`] = contractor.name;
      newValuesMap[`${formikFieldBaseName}.driver.surname`] =
        contractor.surname;
      newValuesMap[`${formikFieldBaseName}.driver.fiscalCodeOrVAT`] =
        contractor.fiscalCodeOrVAT;
      newValuesMap[`${formikFieldBaseName}.driver.address`] =
        contractor.address;
    }

    Object.entries(newValuesMap).forEach(([fieldKey, value]) => {
      setFieldValue(fieldKey, value);
      setFieldError(fieldKey, "");
      setFieldTouched(fieldKey, false, false);
    });
  }, [driver.driverCoincidesWithContractor, contractor]);

  return {
    yesNoOptions,
    disableDriverFieldIfDriverCoincidesWithContractor,
    licenseTypes,
    t,
  };
};
